import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Columns2, Maximize, Minimize, ExternalLink, ArrowLeft } from 'lucide-react';
import { axiosTasklist } from '@/utils/lib/axiosTasklist';
import classNames from 'classnames';
import CompanyHeader from '@/components/company/CompanyHeader';
import useSopState from '@/states/sop.state';
import useTenantState from '@/states/tenant.state';
import BpmnViewer from 'bpmn-js/lib/NavigatedViewer';
import { SecondaryButton } from '@/components/ui/Button';
import MainTable, { MainTableColumn } from '@/components/company/table/MainTable';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';

const TASK_SELECTOR = 'bpmn\\:task, bpmn\\:userTask, bpmn\\:manualTask, bpmn\\:serviceTask, bpmn\\:callActivity, bpmn\\:subProcess, task, userTask, manualTask, serviceTask, callActivity, subProcess';

function getTaskNodeSelector(nodeId: string) {
  return `bpmn\\:task[id="${nodeId}"],
             bpmn\\:userTask[id="${nodeId}"],
             bpmn\\:manualTask[id="${nodeId}"],
             bpmn\\:serviceTask[id="${nodeId}"],
             bpmn\\:callActivity[id="${nodeId}"],
             bpmn\\:subProcess[id="${nodeId}"],
             task[id="${nodeId}"],
             userTask[id="${nodeId}"],
             manualTask[id="${nodeId}"],
             serviceTask[id="${nodeId}"],
             callActivity[id="${nodeId}"],
             subProcess[id="${nodeId}"]`;
}

function parseBpmnTasks(xmlDoc: Document): { role: string; task: string }[] {
  const tasks: { role: string; task: string }[] = [];
  const lanes = xmlDoc.querySelectorAll('bpmn\\:lane, lane');

  if (lanes.length > 0) {
    lanes.forEach(lane => {
      const role = (lane.getAttribute('name') || '-').toLowerCase();
      const flowNodeRefs = lane.querySelectorAll('bpmn\\:flowNodeRef, flowNodeRef');
      flowNodeRefs.forEach(ref => {
        const nodeId = ref.textContent?.trim();
        if (!nodeId) return;
        const node = xmlDoc.querySelector(getTaskNodeSelector(nodeId));
        if (!node) return;
        const taskName = node.getAttribute('name')?.trim();
        if (!taskName) return;
        if (!tasks.some(t => t.task === taskName && t.role === role)) {
          tasks.push({ role, task: taskName });
        }
      });
    });
  } else {
    const participant = xmlDoc.querySelector('bpmn\\:participant, participant');
    const role = (participant?.getAttribute('name') || '-').toLowerCase();
    const allTasks = xmlDoc.querySelectorAll(TASK_SELECTOR);
    allTasks.forEach(node => {
      const taskName = node.getAttribute('name')?.trim();
      if (!taskName) return;
      if (!tasks.some(t => t.task === taskName && t.role === role)) {
        tasks.push({ role, task: taskName });
      }
    });
  }
  return tasks;
}

export default function SOPDetail() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { currentTenant } = useTenantState();
  const { getSopDetail } = useSopState();

  const [viewMode, setViewMode] = useState<'split' | 'table' | 'diagram'>('split');
  const [tasks, setTasks] = useState<{ role: string; task: string }[]>([]);
  const [bpmnXml, setBpmnXml] = useState<string | null>(null);
  const [sopData, setSopData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const viewerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'role',
    order: 'asc',
  });

  const [sortedTasks, setSortedTasks] = useState<{ role: string; task: string }[]>([]);

  const handleExternalLink = (row: any) => {
    const slug = currentTenant?.slug ?? 'default';
    return `https://studio-onprem.merapi.alurkerja.com/${slug}/drive/modeler/bpmn/${row.slug}`;
  };

  useEffect(() => {
    const fetchSopDetail = async () => {
      if (!currentTenant?.id || !slug) return;

      try {
        const data = await getSopDetail(currentTenant.id, slug);
        if (!data) return;
        setSopData(data);

        const files = typeof data.sop_file === 'string' ? JSON.parse(data.sop_file) : data.sop_file;
        const fileUrl = `${axiosTasklist.defaults.cloudStorage}${files[0].path}`;

        const res = await fetch(fileUrl);
        if (!res.ok) throw new Error();

        const blob = await res.blob();
        const xml = await blob.text();

        if (!xml.includes('<?xml')) throw new Error();
        setBpmnXml(xml);
      } catch {
      } finally {
        setLoading(false);
      }
    };

    fetchSopDetail();
  }, [currentTenant?.id, slug, getSopDetail]);

  useEffect(() => {
    if (!bpmnXml) return;

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(bpmnXml, 'text/xml');
      const tasks = parseBpmnTasks(xmlDoc);
      const uniqueTasks = tasks.filter(
        (t, i, self) => i === self.findIndex(u => u.role === t.role && u.task === t.task),
      );

      setTasks(uniqueTasks);
    } catch (err) {
      console.error('Parsing BPMN gagal:', err);
    }
  }, [bpmnXml]);

  useEffect(() => {
    if (!tasks.length) return;

    const sorted = [...tasks].sort((a, b) => {
      const dir = pageConfig.order === 'asc' ? 1 : -1;
      const key = pageConfig.orderBy as keyof typeof a;
      const aVal = (a[key] ?? '').toString().toLowerCase();
      const bVal = (b[key] ?? '').toString().toLowerCase();
      return aVal.localeCompare(bVal) * dir;
    });

    setSortedTasks(sorted);
  }, [tasks, pageConfig]);

  useEffect(() => {
    if (!bpmnXml || !containerRef.current) return;

    if (viewerRef.current) viewerRef.current.destroy();

    const viewer = new BpmnViewer({
      container: containerRef.current,
      additionalModules: [],
    });

    viewer
      .importXML(bpmnXml)
      .then(() => {
        const canvas = viewer.get('canvas') as any;
        canvas.zoom('fit-viewport');
      })
      .catch(() => { });

    viewerRef.current = viewer;
    return () => viewer.destroy();
  }, [bpmnXml]);

  const columns: MainTableColumn[] = [
    {
      name: 'role',
      label: 'Role',
      render: (val: string) => <span>{val || '-'}</span>,
    },
    {
      name: 'task',
      label: 'Task',
      render: (val: string) => <span>{val || '-'}</span>,
    },
  ];

  return (
    <section className="w-full">
      <CompanyHeader
        title={
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-1 rounded-md hover:bg-gray-100 transition-colors"
              title="Kembali"
            >
              <ArrowLeft size={18} />
            </button>
            <span>{`SOP Details - ${sopData?.name}`}</span>
          </div>
        }
        extraAction={
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border overflow-hidden bg-white shadow-sm">
              <button
                className={classNames('p-2', viewMode === 'split' && 'text-primary bg-active')}
                onClick={() => setViewMode('split')}
                title="Split View"
              >
                <Columns2 size={18} />
              </button>
              <button
                className={classNames('p-2', viewMode === 'diagram' && 'text-primary bg-active')}
                onClick={() => setViewMode('diagram')}
                title="Diagram Only"
              >
                <Maximize size={18} />
              </button>
              <button
                className={classNames('p-2', viewMode === 'table' && 'text-primary bg-active')}
                onClick={() => setViewMode('table')}
                title="Table Only"
              >
                <Minimize size={18} />
              </button>
            </div>

            {sopData && (
              <SecondaryButton onClick={() => window.open(handleExternalLink(sopData), '_blank')}>
                <ExternalLink size={16} />
                <span>Open in Modeler</span>
              </SecondaryButton>
            )}
          </div>
        }
      />
      <div className="mx-5 border-b border-gray-200 mb-5" />

      <div className="flex flex-col md:flex-row transition-all duration-300 ease-in-out px-5 h-[calc(100vh-150px)] gap-5">
        <div
          className={classNames('transition-all duration-300 ease-in-out transform overflow-y-auto', {
            'w-full': true,
            'md:w-1/2': viewMode === 'split',
            'md:w-full': viewMode === 'table',
            'md:w-0 md:overflow-hidden': viewMode === 'diagram',
          })}
        >
          <h2 className="text-sm font-semibold mb-2">User Task Mapping</h2>

          <MainTable
            showHeader={false}
            isLoading={loading}
            data={{
              content: sortedTasks as unknown as { [key: string]: unknown }[],
              totalElements: sortedTasks.length,
              totalPages: 1,
            }}
            columns={columns}
            showActions={false}
            pageConfig={pageConfig}
            setPageConfig={setPageConfig}
          />
        </div>

        <div
          className={classNames('transition-all duration-300 ease-in-out transform', {
            'w-full': true,
            'md:w-1/2': viewMode === 'split',
            'md:w-full': viewMode === 'diagram',
            'md:w-0 md:overflow-hidden': viewMode === 'table',
          })}
        >
          <h2 className="text-sm font-semibold mb-2">BPMN Diagram</h2>
          <div
            ref={containerRef}
            className="bg-white rounded-lg shadow-sm p-4 border flex justify-center items-center h-full"
          >
            {loading && <p className="text-gray-400 text-sm">Memuat diagram BPMN...</p>}
            {!loading && !bpmnXml && <p className="text-gray-400 text-sm">Diagram tidak tersedia</p>}
          </div>
        </div>
      </div>
    </section>
  );
}
